import { useState } from "react";
import { Link } from "react-router";
import { Check, ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/lib/auth";
import { usePermissao } from "@/lib/use-permissao";
import { cn } from "@/lib/cn";

type Chave = "precos" | "fichas" | "equipamentos" | "insumos" | "clinicas";

const ITENS: { chave: Chave; titulo: string; descricao: string; para: string }[] = [
  { chave: "precos", titulo: "Definir os preços", descricao: "O pedido da clínica já chega com valor.", para: "/lab/catalogo/gerenciar#precos" },
  { chave: "equipamentos", titulo: "Cadastrar os equipamentos", descricao: "Sugerimos pelas químicas que você trabalha.", para: "/lab/catalogo/gerenciar?adicionar=equipamentos#equipamentos" },
  { chave: "insumos", titulo: "Montar o estoque", descricao: "Insumos sugeridos pelos seus equipamentos e materiais.", para: "/lab/estoque?aba=insumos&adicionar=insumos" },
  { chave: "fichas", titulo: "Criar uma ficha técnica", descricao: "O custo de cada serviço, com os insumos que ele gasta.", para: "/lab/catalogo/gerenciar#servicos" },
  { chave: "clinicas", titulo: "Conectar a primeira clínica", descricao: "Convide quem já manda trabalho pra você.", para: "/lab/cadastros" },
];

const chaveOculto = (orgId: string) => `complete-seu-laboratorio:${orgId}`;
function lerOculto(orgId: string) {
  try {
    return localStorage.getItem(chaveOculto(orgId)) === "1";
  } catch {
    return false;
  }
}

/** "Complete seu laboratório": o que ficou de fora do onboarding curto, no momento de fazer. */
export function CompleteSeuLaboratorio() {
  const { user } = useAuth();
  const orgId = user?.activeOrganization?.id ?? "";
  const pode = usePermissao("labCatalog.manage");
  const [oculto, setOculto] = useState(() => lerOculto(orgId));
  const progresso = trpc.labOnboarding.progresso.useQuery(undefined, { enabled: pode });
  const p = progresso.data;
  if (!pode || !p?.catalogoMontado) return null;
  const feitos = ITENS.filter((i) => p[i.chave]).length;
  if (feitos === ITENS.length) return null;

  // Ocultar encolhe pra uma faixa (com o progresso e "Mostrar"); a escolha fica neste navegador.
  const alternar = () => {
    const novo = !oculto;
    try {
      if (novo) localStorage.setItem(chaveOculto(orgId), "1");
      else localStorage.removeItem(chaveOculto(orgId));
    } catch {
      // sem armazenamento: vale só até recarregar
    }
    setOculto(novo);
  };

  if (oculto) {
    return (
      <button
        type="button"
        onClick={alternar}
        className="flex w-full items-center gap-3 rounded-xl border border-zinc-200 bg-white px-5 py-3 text-left shadow-sm hover:bg-zinc-50 dark:border-border dark:bg-card dark:hover:bg-muted"
      >
        <span className="text-sm font-semibold text-zinc-900 dark:text-foreground">Complete seu laboratório</span>
        <span className="text-xs text-zinc-500 dark:text-muted-foreground">
          {feitos} de {ITENS.length} feitos
        </span>
        <span className="hidden h-1.5 max-w-40 flex-1 overflow-hidden rounded-full bg-zinc-100 sm:block dark:bg-muted">
          <span className="block h-full rounded-full bg-brand-600" style={{ width: `${(feitos / ITENS.length) * 100}%` }} />
        </span>
        <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-brand-700 dark:text-primary">
          Mostrar
          <ChevronDown className="h-3.5 w-3.5" />
        </span>
      </button>
    );
  }

  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-border dark:bg-card">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Complete seu laboratório</h3>
          <p className="text-xs text-zinc-500 dark:text-muted-foreground">
            {feitos} de {ITENS.length} feitos · cada item se marca sozinho quando você conclui.
          </p>
        </div>
        <button
          type="button"
          onClick={alternar}
          className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-muted"
        >
          Ocultar
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-zinc-100 dark:bg-muted">
        <div className="h-full rounded-full bg-brand-600 transition-all" style={{ width: `${(feitos / ITENS.length) * 100}%` }} />
      </div>
      <ul className="divide-y divide-zinc-100 dark:divide-border">
        {ITENS.map((i) => {
          const feito = p[i.chave];
          return (
            <li key={i.chave}>
              <Link to={i.para} className="flex items-center gap-3 py-2.5 hover:bg-zinc-50/60 dark:hover:bg-muted/40">
                <span
                  className={cn(
                    "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                    feito ? "border-emerald-500 bg-emerald-500 text-white" : "border-zinc-300 dark:border-border",
                  )}
                >
                  {feito && <Check className="h-3 w-3" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={cn("block text-sm", feito ? "text-zinc-400 line-through" : "font-medium text-zinc-900 dark:text-foreground")}>{i.titulo}</span>
                  {!feito && <span className="block text-xs text-zinc-500 dark:text-muted-foreground">{i.descricao}</span>}
                </span>
                {!feito && <ChevronRight className="h-4 w-4 shrink-0 text-zinc-300" />}
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
